import {DispatchDataResponse} from "react-acceptjs"
import {DeepPartial, Timestamps} from "types/common"
import {StudentCharge} from "types/student-account/student-ledger"
import {GeneralPayment, LedgerAccount} from "types/student-account/ledger-accounts"
import {Auth} from "types/auth"
import {round, sumBy} from "lodash"
import {PaymentPlan} from "types/student-account/payment-plan"
import {PaymentPeriod} from "types/student-account/payment-period"
import {BankAccount} from "types/student-account/bank-account"

export enum PaymentType {
    Item = "item",
    General = "general"
}

export enum PaymentProvider {
    Manual = "manual",
    Stripe = "stripe",
    AuthorizeNet = "authorize.net",
    Square = "square",
    DirectEmbed = "directembed",
    Paypal = "paypal"
}

export const PAYMENT_METHOD_OPTIONS = [
    {id: "cash", name: "Cash"},
    {id: "card", name: "Credit Card"},
    {id: "paypal", name: "Paypal"},
    {id: "ach_debit", name: "ACH Debit"},
    {id: "bank_debit", name: "Bank Debit"},
    {id: "apple_pay", name: "Apple Pay"},
    {id: "google_pay", name: "Google Pay"},
    {id: "wise_transfer", name: "Wise Transfer"},
    {id: "pell", name: "PELL"},
    {id: "sub", name: "DL (Sub)"},
    {id: "unsub", name: "DL (Unsub)"},
    {id: "fseog", name: "FSEOG"},
    {id: "transfer", name: "Transfer"},
    {id: "check", name: "Check"},
    {id: "tuition_adjustment", name: "Tuition Adjustment"},
    {id: "wire_transfer", name: "Wire Transfer"}
]

export type StudentPaymentSupportedCurrency = "usd" | "cad"

export const paymentCurrencyOptions: Array<{id: StudentPaymentSupportedCurrency; name: string}> = [
    {id: "usd", name: "USD"},
    {id: "cad", name: "CAD"}
]

export const STUDENT_PAYMENT_STATUSES = ["pending", "processing", "success", "canceled", "failed", "returned"] as const

export type StudentPaymentStatus = typeof STUDENT_PAYMENT_STATUSES[number]

export type StudentPayment = {
    paymentId: number
    generalPaymentId?: number | null
    generalPayment?: GeneralPayment // populate
    studentProfileId: number
    studentProfile?: Auth.LinkedProfileDetails // populate
    provider: PaymentProvider
    providerIntentId: string
    providerPaymentMethodId?: string | null // Stripe pm_xxx used for this charge
    amountInCents: number
    processingFeeAmountInCents?: number | null
    currency: StudentPaymentSupportedCurrency
    notes: string
    status: StudentPaymentStatus
    statusUpdatedAt?: string | null
    createdByProfileId: number
    createdByProfile?: Auth.LinkedProfileDetails // populate
    updatedByProfileId: number | null
    updatedByProfile?: Auth.LinkedProfileDetails // populate
    receiptDocumentId?: number | null
    metadata?: Record<string, any> | string | null
    isRefund?: boolean | 0 | 1
    isStipends?: boolean | 0 | 1
    isVoided?: boolean | 0 | 1
    failureReason?: string | null
    returnReason?: string | null

    tranxDate: string
    postedDate?: string | null
    receiptPrintedDate?: string | null
    bankAccountId?: number | null
    bankAccount?: BankAccount // populate
    accountId?: number | null
    account?: LedgerAccount // populate
    extraAmount?: number | null
    extraDescription?: string | null
    academicYear?: number | null
    awardYear?: number | null
    methodOfPayment?: string | null
    receiptId?: string | null
    disbursementId?: number | null
    periodId?: number | null
    period?: PaymentPeriod
    paymentCharges?: StudentPaymentCharge[] // populate
    charges?: StudentCharge[] // populate
    paymentPlanItems?: StudentPaymentPlanItem[] // populate
    paymentPlans?: PaymentPlan[] // populate

    f1098tAmountBox1?: number | null
    f1098tAmountBox4?: number | null
    f1098tAmountBox5?: number | null
    f1098tAmountBox6?: number | null
    f1098tAmountBox10?: number | null

    sisId?: string | null
    sisId2?: string | null
    // internal
    amount?: number
    processingFeeAmount?: number | null
} & Timestamps

export type StudentPaymentCharge = {
    paymentId: number
    chargeId: number
    amount: number
    accountId?: number | null
}

export type StudentPaymentPlanItem = {
    paymentId: number
    paymentPlanId: number
    paymentPlanItemId: number
    amount: number
    accountId?: number | null
}

export type StudentPaymentRefundStatus = "pending" | "succeeded" | "failed" | "canceled"

export type StudentPaymentRefund = {
    id: number
    paymentId: number
    provider: string
    externalRefundId?: string | null
    amountInCents: number
    reason?: string | null
    status: StudentPaymentRefundStatus
    refundDate?: string | null
    refundedByProfileId?: number | null
}

export type RefundStudentPaymentPayload = {
    paymentId: number
    /** Amount to refund in cents. Omit for a full refund. */
    amountInCents?: number
    reason?: string
}

export type CreateStripePaymentIntentApiPayload = {
    studentProfileId?: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    paymentMethodId?: number
    generalPaymentId?: number
    notes?: string
    payerFullName?: string
    academicYear?: number
    paymentPlanItems?: Partial<StudentPaymentPlanItem>[]
}

export type CreatePapalOrderApiPayload = {
    studentProfileId?: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    generalPaymentId?: number
    notes?: string
    payerFullName?: string
    academicYear?: number
    paymentPlanItems?: Partial<StudentPaymentPlanItem>[]
    createdFromPlatform?: PaymentCreatedFromPlatform
}

export type CapturePaypalOrderPaymentApiPayload = {
    paymentId: number
    orderId: string
}
export type CreateSquarePaymentApiPayload = {
    studentProfileId?: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    generalPaymentId?: number
    notes?: string
    payerFullName?: string
    academicYear?: number
    paymentPlanItems?: Partial<StudentPaymentPlanItem>[]
    sourceId: string
}

export enum PaymentCreatedFromPlatform {
    EdularWeb = "EDULAR-WEB",
    EdularMobile = "EDULAR-MBL",
    Others = "OTHERS"
}

export type CreateAuthorizeNetStudentPaymentApiPayload = {
    studentProfileId?: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    notes?: string
    payerFullName?: string
    academicYear?: number
    generalPaymentId?: number
    paymentPlanItems?: Partial<StudentPaymentPlanItem>[]
    createdFromPlatform?: PaymentCreatedFromPlatform
}

export type CompleteAuthorizeNetStudentPaymentApiPayload = {
    studentProfileId?: number
    paymentId: number
    payerFirstName: string
    payerLastName: string
    country: string
    state: string
    zip: string
    authorizeNetPaymentNonce: DispatchDataResponse
}

export type CreateDirectEmbedStudentPaymentApiPayload = {
    studentProfileId?: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    notes?: string
    payerFullName?: string
    academicYear?: number
    generalPaymentId?: number
    paymentPlanItems?: Partial<StudentPaymentPlanItem>[]
    createdFromPlatform?: PaymentCreatedFromPlatform
}

export type MakeDirectEmbedStudentPaymentApiPayload = {
    studentProfileId?: number
    paymentId: number
    tokenizedCard: string
    payerFullName: string
    tokenResponse: any
    isPaymentRecurring?: boolean
    cvv?: string
}

export type TokenizeCardDirectEmbedStudentPaymentAPIPayload = {
    paymentId: number
    cardNumber: string
    cardExpMonth: string
    cardExpYear: string
    cardCvv: string
    billingDetails: {
        payerFullName: string
        address: {
            street?: string
            city?: string
            state?: string
            zipcode: string
            country?: string
        }
    }
}

export type CurrencyExchangeRates = {[currecny: string]: number}

export enum StudentPaymentLinkedEntity {
    Profile = "profile",
    GeneralPayment = "general-payment",
    Account = "account",
    BankAccount = "bank-account",
    Period = "period",
    Charges = "charges",
    PaymentPlan = "payment-plans",
    PaymentPlanCharges = "payment-plans-charges",
    Refunds = "refunds"
}

export const getPaymentAmountDue = (
    payment: DeepPartial<StudentPayment> | undefined,
    currencyExchangeRates: CurrencyExchangeRates,
    includesExtraAmount = true,
    includesPaymentCharges = true,
    includesPaymentPlanItems = true
) => {
    if (!payment) return 0
    const paymentAmountInUSD = round(
        ((payment.amountInCents || 0) / 100 - (includesExtraAmount ? payment.extraAmount || 0 : 0)) /
            (currencyExchangeRates[payment.currency?.toUpperCase()] || 1),
        2
    )
    return Math.max(
        0,
        paymentAmountInUSD -
            (includesPaymentCharges ? sumBy(payment.paymentCharges, "amount") : 0) -
            (includesPaymentPlanItems ? sumBy(payment.paymentPlanItems, "amount") : 0)
    )
}

export enum RecurringPaymentCollectionMethod {
    ChargeAutomatically = "charge_automatically",
    SendInvoice = "send_invoice"
}

export enum RecurringPaymentStatus {
    Active = "active",
    Canceled = "canceled",
    Expired = "expired",
    PastDue = "past_due"
}

export enum RecurringPaymentInterval {
    Day = "day",
    Week = "week",
    Month = "month",
    Year = "year"
}

export type StudentRecurringPayment = Timestamps & {
    recurringPaymentId: number
    profileId: number
    provider: PaymentProvider
    externalId: string

    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    status: RecurringPaymentStatus
    cancelAt?: string | null // the date when the subscription will be canceled
    canceledAt?: string | null // the date when the subscription was canceled
    endedAt?: string | null // the date when the subscription ended

    startAt?: string | null // the date when the subscription was first created
    billingStartAt?: string | null // the date of the first full invoice
    lastBilledAt?: string | null // current_period_start
    nextBillingAt?: string | null // current_period_end

    intervalCount?: number // number of intervals between billings
    trialStart?: string | null // Stripe trial_start
    trialEnd?: string | null // Stripe trial_end
    collectionMethod?: RecurringPaymentCollectionMethod | null
    description?: string | null
    metadata?: Record<string, any> | null
    quantity?: number | null
    stripeCustomerId?: string | null // Stripe cus_xxx ID

    priceId?: string | null
    lastInvoiceId?: string | null // the last invoice id for the subscription
    paymentMethodId?: number | null // default student payment method for the subscription
    generalPaymentId?: number | null // linked GeneralPayment item
    paymentPlanItems?: StudentRecurringPaymentPlanItem[] // populate
}

export type StudentRecurringPaymentPlanItem = {
    id?: number
    recurringPaymentId?: number
    paymentPlanId: number
    paymentPlanItemId: number
    amount: number
    accountId?: number | null
}

export type PreparePaypalRecurringSubscriptionApiPayload = {
    profileId: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    cancelAt?: string | null
    generalPaymentId?: number
    paymentPlanItems?: Partial<StudentRecurringPaymentPlanItem>[]
}

export type PreparePaypalRecurringSubscriptionApiResponse = {
    recurringPaymentId: number
    paypalPlanId: string
}

export type ActivatePaypalRecurringSubscriptionApiPayload = {
    recurringPaymentId: number
    subscriptionId: string
}

export enum StudentPaymentMethodType {
    Card = "card",
    UsBankAccount = "us_bank_account",
    Ach = "ach"
}

export type StudentPaymentMethod = Timestamps & {
    paymentMethodId: number
    profileId: number
    provider: PaymentProvider
    type: StudentPaymentMethodType
    externalId: string
    isActive?: boolean | 0 | 1

    billingCountry?: string | null
    billingState?: string | null
    billingCity?: string | null
    billingPostalCode?: string | null
    billingAddressLine1?: string | null
    billingAddressLine2?: string | null

    last4?: string | null
    expMonth?: number | null
    expYear?: number | null
    cardBrand?: string | null
    cardHolderName?: string | null

    bankName?: string | null
    bankAccountType?: string | null
    bankAccountName?: string | null
    bankAccountNumber?: string | null
    bankRoutingNumber?: string | null
}

export type CreateRecurringPaymentPayload = Partial<StudentRecurringPayment> & {
    generalPaymentId?: number
    notes?: string
    payerFullName?: string
    academicYear?: number
    paymentPlanItems?: Partial<StudentRecurringPaymentPlanItem>[]
}
